import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Collapse,
  Button,
  Table,
  UncontrolledTooltip,
} from 'reactstrap';
import FormFooter from '../../../../common/forms/FormFooter';
import Badge from '../../../../common/Badge';
import { tzNormalizeDate } from '../../../../../utils/date';
import { DATE_TIME_FORMAT } from '../../../../../config/locale';

const PATH_LABELS = {
  START_CIRCUIT: 'Iniciar circuito',
  COMPLETE_CIRCUIT: 'Completar circuito',
};

const MAX_BUSES_IN_TOOLTIP = 30;

const getPathLabel = (path) =>
  path ? PATH_LABELS[path] || path : 'sin camino';

const formatBusList = (buses) => {
  const codes = buses.map((bus) => bus.companyBusId || bus.busId);
  if (codes.length <= MAX_BUSES_IN_TOOLTIP) return codes.join(', ');
  return `${codes.slice(0, MAX_BUSES_IN_TOOLTIP).join(', ')} +${
    codes.length - MAX_BUSES_IN_TOOLTIP
  } más`;
};

const TraceFilterStep = ({ step, stepKey }) => {
  const [open, setOpen] = useState(false);
  const [openAccepted, setOpenAccepted] = useState(false);
  const hasDiscardedBuses =
    Array.isArray(step.discardedBuses) && step.discardedBuses.length > 0;
  const hasAcceptedBuses =
    Array.isArray(step.acceptedBuses) && step.acceptedBuses.length > 0;
  const isBypassed = !!step.bypassed;
  return (
    <div className="mb-3 pb-3 border-bottom">
      <div className="d-flex justify-content-between align-items-start">
        <span>
          <strong>{step.name}</strong>{' '}
          {step.relaxed && <Badge text="regla relajada" color="warning" />}{' '}
          {isBypassed && <Badge text="se ignoró" color="secondary" />}
        </span>
        <span className="font-monospace">
          {step.before} &rarr; -{step.discarded} &rarr; {step.after}
        </span>
      </div>
      <div className="progress mt-2">
        {isBypassed ? (
          <div
            id={`${stepKey}-bypassed`}
            className="progress-bar bg-secondary"
            role="progressbar"
            style={{ width: '100%' }}
          />
        ) : (
          <>
            <div
              id={`${stepKey}-ok`}
              className="progress-bar bg-success"
              role="progressbar"
              style={{ width: `${(step.after / (step.before || 1)) * 100}%` }}
            />
            <div
              id={`${stepKey}-ko`}
              className="progress-bar bg-danger"
              role="progressbar"
              style={{
                width: `${(step.discarded / (step.before || 1)) * 100}%`,
              }}
            />
          </>
        )}
      </div>
      {isBypassed && (
        <UncontrolledTooltip placement="top" target={`${stepKey}-bypassed`}>
          Ningún bus cumplió esta regla; se ignoró para no dejar el itinerario
          sin sugerencia.
        </UncontrolledTooltip>
      )}
      {!isBypassed && step.after > 0 && (
        <UncontrolledTooltip placement="top" target={`${stepKey}-ok`}>
          {hasAcceptedBuses
            ? formatBusList(step.acceptedBuses)
            : `${step.after} bus(es) continúan`}
        </UncontrolledTooltip>
      )}
      {!isBypassed && step.discarded > 0 && hasDiscardedBuses && (
        <UncontrolledTooltip placement="top" target={`${stepKey}-ko`}>
          {formatBusList(step.discardedBuses)}
        </UncontrolledTooltip>
      )}
      <p className="text-muted mb-2">{step.description}</p>
      {hasAcceptedBuses && (
        <>
          <Button
            color="link"
            size="sm"
            className="p-0 text-muted small mr-3"
            onClick={() => setOpenAccepted(!openAccepted)}
          >
            Ver {step.acceptedBuses.length} aceptado(s)
          </Button>
          <Collapse isOpen={openAccepted}>
            <p className="mt-2 mb-0">
              <i className="fa fa-check-circle text-success" />{' '}
              {step.acceptedBuses
                .map((bus) => bus.companyBusId || bus.busId)
                .join(', ')}
            </p>
          </Collapse>
        </>
      )}
      {hasDiscardedBuses && (
        <>
          <Button
            color="link"
            size="sm"
            className="p-0 text-muted small"
            onClick={() => setOpen(!open)}
          >
            Ver {step.discardedBuses.length} descartado(s)
          </Button>
          <Collapse isOpen={open}>
            <Table size="sm" className="mt-2">
              <thead>
                <tr>
                  <th>Bus</th>
                  <th>Razón</th>
                </tr>
              </thead>
              <tbody>
                {step.discardedBuses.map((discardedBus) => (
                  <tr key={discardedBus.busId}>
                    <td>{discardedBus.companyBusId || discardedBus.busId}</td>
                    <td>{discardedBus.reason}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Collapse>
        </>
      )}
    </div>
  );
};

TraceFilterStep.propTypes = {
  step: PropTypes.shape({
    order: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    relaxed: PropTypes.bool,
    bypassed: PropTypes.bool,
    before: PropTypes.number,
    discarded: PropTypes.number,
    after: PropTypes.number,
    discardedBuses: PropTypes.arrayOf(
      PropTypes.shape({
        busId: PropTypes.number,
        companyBusId: PropTypes.string,
        reason: PropTypes.string,
      }),
    ),
    acceptedBuses: PropTypes.arrayOf(
      PropTypes.shape({
        busId: PropTypes.number,
        companyBusId: PropTypes.string,
      }),
    ),
  }).isRequired,
  stepKey: PropTypes.string.isRequired,
};

const OUTCOME_LABELS = {
  SELECTED: 'seleccionado',
  EXHAUSTED: 'agotado',
};

const TraceAttempt = ({ attempt, rowId }) => {
  const selected = attempt.outcome === 'SELECTED';
  const [open, setOpen] = useState(selected);
  return (
    <div className="mb-2 pb-2 border-bottom">
      <Button
        color="link"
        className={`p-0 d-flex justify-content-between align-items-center w-100 text-decoration-none ${
          selected ? 'text-success' : 'text-muted'
        }`}
        onClick={() => setOpen(!open)}
      >
        <span>
          {selected && <i className="fa fa-check-circle mr-1" />}
          Nivel {attempt.level} &middot; {attempt.label}
        </span>
        <span className="small text-nowrap ml-2">
          {OUTCOME_LABELS[attempt.outcome] || attempt.outcome} (
          {attempt.candidates} candidato{attempt.candidates === 1 ? '' : 's'})
          {selected && ' ← usado'}
        </span>
      </Button>
      <Collapse isOpen={open}>
        <div className="mt-2 pl-3">
          {(attempt.steps || []).map((step, idx) => (
            <TraceFilterStep
              // eslint-disable-next-line react/no-array-index-key
              key={step.order != null ? step.order : `${attempt.level}-${idx}`}
              step={step}
              stepKey={`trace-attempt-${rowId}-${attempt.level}-${
                step.order != null ? step.order : idx
              }`}
            />
          ))}
        </div>
      </Collapse>
    </div>
  );
};

TraceAttempt.propTypes = {
  attempt: PropTypes.shape({
    level: PropTypes.number,
    label: PropTypes.string,
    relaxed: PropTypes.bool,
    outcome: PropTypes.string,
    candidates: PropTypes.number,
    steps: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  rowId: PropTypes.number,
};

TraceAttempt.defaultProps = {
  rowId: 0,
};

const ItinerarySuggestionTraceModal = ({
  trace,
  row,
  suggested,
  onManualAssign,
  onClose,
}) => {
  const [attemptsOpen, setAttemptsOpen] = useState(false);

  if (!trace) return null;

  const attempts = trace.attempts || [];
  const initialCandidates = trace.initialCandidates || 0;
  const finalCandidates = trace.finalCandidates || 0;

  const alert = suggested ? (
    <Alert color="success">
      <i className="fa fa-check-circle" /> De {initialCandidates} buses
      elegibles, {finalCandidates} llegaron a la selección final &middot;
      camino: {getPathLabel(trace.path)}
    </Alert>
  ) : (
    <Alert color="danger">
      <i className="fa fa-exclamation-triangle" /> De {initialCandidates} buses
      elegibles, {finalCandidates} quedaron disponibles &middot; camino:{' '}
      {getPathLabel(trace.path)}
      {trace.message && (
        <>
          <br />
          <small>{trace.message}</small>
        </>
      )}
    </Alert>
  );

  const footer = suggested ? (
    <div className="mt-2">
      <Button color="secondary" outline onClick={onClose}>
        Cerrar
      </Button>
    </div>
  ) : (
    <FormFooter
      saveButtonAction={onManualAssign}
      saveButtonType="button"
      saveButtonText="Asignar bus manualmente"
      saveButtonIcon="fa fa-bus"
    >
      <Button color="secondary" outline onClick={onClose}>
        Cerrar
      </Button>
    </FormFooter>
  );

  return (
    <div>
      <p className="text-muted mb-3">
        {row.sourceLocationName} &rarr; {row.destinationLocationName}
        {' · '}
        {tzNormalizeDate({
          date: row.departuretime,
          format: DATE_TIME_FORMAT,
        })}
      </p>

      {alert}

      <h5>Recorrido del filtrado</h5>
      {(trace.filters || []).map((step) => (
        <TraceFilterStep
          key={step.order}
          step={step}
          stepKey={`trace-step-${row.id}-${step.order}`}
        />
      ))}

      {attempts.length > 0 && (
        <div className="mb-3">
          <Button
            color="link"
            size="sm"
            className="p-0 mb-2"
            onClick={() => setAttemptsOpen(!attemptsOpen)}
          >
            <i className={`fa fa-caret-${attemptsOpen ? 'down' : 'right'}`} />{' '}
            Ver intentos de la cascada ({attempts.length})
          </Button>
          <Collapse isOpen={attemptsOpen}>
            {attempts.map((attempt) => (
              <TraceAttempt
                key={attempt.level}
                attempt={attempt}
                rowId={row.id}
              />
            ))}
          </Collapse>
        </div>
      )}

      {footer}
    </div>
  );
};

ItinerarySuggestionTraceModal.propTypes = {
  trace: PropTypes.shape({
    path: PropTypes.string,
    initialCandidates: PropTypes.number,
    finalCandidates: PropTypes.number,
    message: PropTypes.string,
    filters: PropTypes.arrayOf(PropTypes.shape({})),
    attempts: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  row: PropTypes.shape({
    id: PropTypes.number,
    sourceLocationName: PropTypes.string,
    destinationLocationName: PropTypes.string,
    departuretime: PropTypes.number,
  }).isRequired,
  suggested: PropTypes.bool,
  onManualAssign: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ItinerarySuggestionTraceModal.defaultProps = {
  trace: null,
  suggested: false,
};

export default ItinerarySuggestionTraceModal;
